import { createFileRoute, redirect } from "@tanstack/react-router";
import LoadingPage from "lib/components/layout/LoadingPage";
import ShowroomConfigure from "lib/components/showroom2/pages/ShowroomConfigure";
import ShowroomPlanViewerOnly from "lib/components/showroom2/pages/ShowroomPlanViewerOnly";
import { ShowroomProvider } from "lib/components/showroom2/state/useShowroomState";
import { errorPath, useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";

/**
 * Public buyer configurator for an unsaved plan. Loads a master / community
 * plan via the `public-plans` API and surfaces the CTA modal that creates a
 * new `HomePlan` on submit.
 */
function ShowroomConfigurePage() {
  const { builderLoading } = useUserContext();
  const { builderId, planId, lotId, showroomPlanViewerOnly } = useAppRouterParams();

  if (builderLoading) {
    return <LoadingPage pageTitle="Showroom" />;
  }

  const showroomKey = [builderId, planId, lotId].filter(Boolean).join("-") || undefined;
  const planViewerOnly = showroomPlanViewerOnly === "true";

  return (
    <ShowroomProvider key={showroomKey} version="sales">
      {planViewerOnly ? <ShowroomPlanViewerOnly /> : <ShowroomConfigure />}
    </ShowroomProvider>
  );
}

export const Route = createFileRoute("/showroom/")({
  // Gate at the route level so the check is tied to route resolution, not a
  // component effect that can fire mid-transition with stale `enabledFeatures`.
  // Defer until `RouterContextSync` flips `flagsHydrated` and calls
  // `router.invalidate()`.
  beforeLoad: ({ context }) => {
    if (!context.flagsHydrated) {
      return;
    }
    if (!context.enabledFeatures.showroomUnification) {
      throw redirect({ to: errorPath(403, undefined, "This feature is not enabled for your account.") });
    }
    if (!context.enabledProducts.showroom) {
      throw redirect({
        to: errorPath(403, undefined, "The product you are trying to access is not enabled for this organization."),
      });
    }
  },
  component: ShowroomConfigurePage,
  staticData: { isPublicPage: true },
});
